import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { Typography, Form, Row, Col, Input, Radio } from "antd"
import { validatePhoneNumber } from "ui/Input/PhoneInput"
import { useTranslation } from "react-i18next"
import { PhoneInput } from "ui/Input"
import { handleNameKeyDown, handleFirstNamePaste, handleFirstNameInput } from "utils/InputSanitizers"
import { handleLastNameBlur, handleLastNameInput, handleLastNameKeyPress } from "pagesV2/pro/utils/ProUtils"

const { Text } = Typography
const { TextArea } = Input

const ContactInformationBlock = ({ form, setPhoneNumber, phoneNumber, initialValues }) => {
  const [t] = useTranslation()
  const accessRequirement = Form.useWatch("accessibility_enabled", form)
  useEffect(() => {
    if (initialValues) {
      if (initialValues?.phone_number && initialValues.country_code) {
        form.setFieldsValue({
          phone_number: {
            countryCode: initialValues.country_code,
            fullNumber: initialValues.phone_number
          }
        })
        setPhoneNumber({ countryCode: initialValues.country_code, fullNumber: initialValues.phone_number })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, setPhoneNumber, initialValues])

  return (
    <>
      <Text style={{ fontSize: "18px", fontWeight: 600 }}>{t("webIntegration.contact_information")}</Text>
      <Row gutter={[16, 16]} style={{ marginTop: "24px" }}>
        <Col xs={24} sm={12}>
          <Form.Item
            name="first_name"
            label="First Name"
            normalize={(value) => value?.trim()}
            rules={[
              { required: true, message: "Please enter your first name" },
              {
                pattern: /^[A-Za-z\s-]+$/,
                message: "First name should contain only letters"
              }
            ]}
          >
            <Input
              placeholder="First Name"
              onKeyDown={handleNameKeyDown}
              onInput={handleFirstNameInput}
              onPaste={(e) => handleFirstNamePaste(e, (text) => form.setFieldsValue({ first_name: text }))}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="last_name"
            label="Last Name"
            normalize={(value) => value?.trim()}
            rules={[
              { required: true, message: "Please enter your last name" },
              {
                pattern: /^[A-Za-z\s-]+$/,
                message: "Last name should contain only letters"
              }
            ]}
          >
            <Input
              placeholder="Last Name"
              onKeyDown={handleLastNameKeyPress}
              onInput={handleLastNameInput}
              onBlur={(e) => handleLastNameBlur(e, form)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="email"
            label="Email"
            normalize={(value) => value?.trim()}
            rules={[
              { required: true, message: "Please enter your email" },
              { type: "email", message: "Enter a valid email" }
            ]}
          >
            <Input placeholder="Email" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={12}>
          <Form.Item
            name="phone_number"
            label="Phone Number"
            required={true}
            rules={[
              {
                validator: (_, value) => {
                  if (!value?.fullNumber) {
                    return Promise.reject(t("form_validation.phone_required"))
                  }
                  return validatePhoneNumber(value.fullNumber)
                }
              }
            ]}
          >
            <PhoneInput
              initialValue={phoneNumber?.fullNumber}
              initialCountryCode={phoneNumber?.countryCode}
              onChange={(val) => {
                if (val) {
                  form.setFieldsValue({ phone_number: val })
                  setPhoneNumber(val)
                }
              }}
            />
          </Form.Item>
        </Col>
      </Row>
      <div style={{ marginTop: "32px" }}>
        <Text>{t("webIntegration.party_access_required")}</Text>
      </div>
      <Form.Item name="accessibility_enabled" style={{ marginTop: "20px" }} initialValue={false}>
        <Radio.Group
          options={[
            { value: true, label: "Yes" },
            { value: false, label: "No" }
          ]}
        />
      </Form.Item>
      {accessRequirement && (
        <Form.Item name="accessibility_notes" label="Notes" rules={[{ required: true, message: "Please provide details" }]}>
          <TextArea rows={4} placeholder="Describe access requirements..." />
        </Form.Item>
      )}
    </>
  )
}

export default ContactInformationBlock

ContactInformationBlock.propTypes = {
  form: PropTypes.object,
  phoneNumber: PropTypes.string,
  setPhoneNumber: PropTypes.func,
  initialValues: PropTypes.object
}
